/** @jsx jsx */
import React, { useState, useEffect, useMemo } from "react";
import { jsx } from "@emotion/core";
import { Formik, Field } from "formik";
import { Redirect } from "react-router";

import { get, post } from "../api";

import makeSelectOptionsFromKeyValuePairs from "../../utils/makeSelectOptionsFromKeyValuePairs";

import PrimaryCTAButton from "../../components/PrimaryCTA";
import Form from "../../components/Form";
import FormField, { FormFieldRow } from "../../components/FormField";
import { FormikInput, FormikSearchableDropdown } from "../../components/Input";
import Label from "../../components/Label";
import PageContainer from "../components/PageContainer";

import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";


const initialValues = {
    qualificationType: null,
    courseTitle: null,
    startYear: null,
    graduationYear: null,
};

const validator = values => {
    let errors = {};

    if (!values.qualificationType) {
        errors.qualificationType = "Required";
    }

    if (!values.courseTitle) {
        errors.courseTitle = "Required";
    }

    if (!values.startYear) {
        errors.startYear = "Required";
    } else if (
        // regex to test for a string of 4 digits
        !/^\d{4}$/.test(values.startYear)
    ) {
        errors.startYear = "Must be a 4-digit year";
    } else if (!(values.startYear > 1900)) {
        errors.startYear = "Please check start year";
    }

    if (!values.graduationYear) {
        errors.graduationYear = "Required";
    } else if (
        // regex to test for a string of 4 digits
        !/^\d{4}$/.test(values.graduationYear)
    ) {
        errors.graduationYear = "Must be a 4-digit year";
    } else if (values.graduationYear > 2099) {
        errors.graduationYear = "Please check graduation year";
    }

    return errors;
};

const getDegreeTypes = async (
    setIsLoggedIn,
    setIsLoading,
    setError,
    setQualificationTypeOptions
) => {
    await handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.currentStudy.getDegreeTypes }),
        setIsLoggedIn,
        setIsLoading,
        setError,
        setData: setQualificationTypeOptions,
        dataTransform: makeSelectOptionsFromKeyValuePairs,
    });
};

const submitCurrentStudy = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    const body = {
        degreeType: values.qualificationType,
        degreeCourse: values.courseTitle,
        yearCourseStart: values.startYear,
        yearCourseEnd: values.graduationYear,
        applicationStep: "DEGREE",
    };

    await handlePost({
        postApiCall: () =>
            post({ url: resourceUrls.currentStudy.submit, body: body }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const CurrentStudy = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [qualificationTypeOptions, setQualificationTypeOptions] = useState(
        null
    );

    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    const currentStudyTitle = useMemo(() => {
        switch (localStorage.studyingStatus) {
            case 'Currently studying':
                return 'What are you studying?';

            case 'Recently graduated':
                return 'What did you study?';
        
            default:
                return 'What are/were you studying?'
        }
    }, []);

    useEffect(() => {
        getDegreeTypes(
            setIsLoggedIn,
            setIsLoading,
            setError,
            setQualificationTypeOptions
        );
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2 data-testid="current-study-title">{currentStudyTitle}</h2>
            <Formik
                initialValues={initialValues}
                validate={validator}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={submitCurrentStudy(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ handleSubmit, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <FormField>
                            <Label htmlFor="qualificationType">
                                Qualification type
                            </Label>
                            <Field
                                id="qualificationType"
                                data-testid="current-study-qualification-type"
                                name="qualificationType"
                                options={qualificationTypeOptions}
                                component={FormikSearchableDropdown}
                            />
                        </FormField>
                        <FormField>
                            <Label htmlFor="courseTitle">Course title</Label>
                            <Field
                                id="courseTitle"
                                data-testid="current-study-course-title"
                                name="courseTitle"
                                component={FormikInput}
                            />
                        </FormField>
                        <FormField>
                            <FormFieldRow>
                                <FormField>
                                    <Label htmlFor="startYear">
                                        Start year
                                    </Label>
                                    <Field
                                        id="startYear"
                                        data-testid="current-study-start-year"
                                        name="startYear"
                                        component={FormikInput}
                                    />
                                </FormField>
                                <FormField>
                                    <Label htmlFor="graduationYear">
                                        Year of graduation
                                    </Label>
                                    <Field
                                        id="graduationYear"
                                        data-testid="current-study-graduation-year"
                                        name="graduationYear"
                                        component={FormikInput}
                                    />
                                </FormField>
                            </FormFieldRow>
                        </FormField>
                        <PrimaryCTAButton
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="next-button"
                        >
                            Next →
                        </PrimaryCTAButton>
                    </Form>
                )}
            />
        </PageContainer>
    );
};

export default CurrentStudy;
